import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';
import ImageUpload from '../../../components/ImageUpload';
import { showingbookerAPI } from '../../../services/showingbookerAPI';
import EditPropertyForm from './EditPropertyForm';

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 2px solid #e9ecef;
  padding: 20px;
  margin-bottom: 20px;
  transition: border-color 0.2s ease;
  
  &:hover {
    border-color: #007AFF;
  }
`;

const PropertyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const PropertyTitle = styled.div`
  display: flex;
  align-items: center;
`;

const PropertyIcon = styled.span`
  font-size: 20px;
  margin-right: 10px;
`;

const PropertyAddress = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 18px;
  padding: 5px;
  border-radius: 4px;
  
  &:hover {
    background: #f8d7da;
  }
`;

const PropertyPhoto = styled.div`
  margin: 15px 0 20px 0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
  }
  
  .photo-placeholder {
    width: 100%;
    height: 120px;
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #666;
    text-align: center;
    
    .placeholder-icon {
      font-size: 32px;
      margin-bottom: 8px;
    }
    
    .placeholder-text {
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

const PropertyDetails = styled.div`
  margin-bottom: 20px;
  color: #666;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const InlineSelect = styled.select`
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  cursor: pointer;
`;

const EditableText = styled.textarea`
  width: 60%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
`;

const BookingLinkContainer = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
  padding: 12px;
  margin: 15px 0;
`;

const BookingLinkText = styled.div`
  font-size: 12px;
  color: #0066cc;
  word-break: break-all;
  margin-bottom: 8px;
  font-family: monospace;
`;

const LinkButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const LinkButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &.copy {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.visit {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #1e7e34;
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0;
`;

const StatBox = styled.div`
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #007AFF;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ContactsSection = styled.div`
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #17a2b8;
`;

const ContactsHeader = styled.div`
  font-weight: bold;
  color: #17a2b8;
  font-size: 16px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContactsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const ContactColumn = styled.div`
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e9ecef;
`;

const ContactTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #495057;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ContactInfo = styled.div`
  margin-bottom: 12px;
  
  .name {
    font-weight: 500;
    color: #333;
    font-size: 14px;
    margin-bottom: 4px;
  }
  
  .details {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
  }
  
  .no-info {
    color: #999;
    font-style: italic;
    font-size: 12px;
  }
`;

const ContactActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ContactManagement = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
`;

const ManageButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &.add {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #1e7e34;
    }
  }
  
  &.edit {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
`;

const CommButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.call {
    color: #28a745;
    border-color: #28a745;
    
    &:hover {
      background: #28a745;
      color: white;
    }
  }
  
  &.sms {
    color: #007AFF;
    border-color: #007AFF;
    
    &:hover {
      background: #007AFF;
      color: white;
    }
  }
  
  &.email {
    color: #6f42c1;
    border-color: #6f42c1;
    
    &:hover {
      background: #6f42c1;
      color: white;
    }
  }
  
  .icon {
    font-size: 14px;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &.primary {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #dee2e6;
    
    &:hover {
      background: #e9ecef;
    }
  }
  
  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const WarningBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 15px;
  color: #856404;
  margin-top: 15px;
`;

// Owner Selector Modal Components
const SelectorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const SelectorContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const SelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 15px;
`;

const SearchBox = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const CreateNewButton = styled.button`
  width: 100%;
  padding: 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: #1e7e34;
  }
`;

const OwnersList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
`;

const OwnerItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8f9fa;
  }
  
  .name {
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
  }
  
  .details {
    font-size: 12px;
    color: #666;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-style: italic;
`;

const QRModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const QRModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  h3 {
    margin: 0 0 20px 0;
    color: #333;
    font-size: 20px;
  }
  
  .qr-container {
    margin: 20px 0;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    
    img {
      max-width: 100%;
      height: auto;
    }
  }
  
  .property-info {
    margin: 15px 0;
    padding: 15px;
    background: #e3f2fd;
    border-radius: 6px;
    font-size: 14px;
    color: #666;
  }
  
  .buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 20px;
  }
  
  button {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    
    &.download {
      background: #007AFF;
      color: white;
      
      &:hover {
        background: #0056b3;
      }
    }
    
    &.close {
      background: #6c757d;
      color: white;
      
      &:hover {
        background: #545b62;
      }
    }
  }
`;

const EditModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const EditModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  
  h3 {
    margin: 0 0 25px 0;
    color: #333;
    font-size: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const EditForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormSection = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  
  h4 {
    margin: 0 0 15px 0;
    color: #007AFF;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 15px;
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  
  label {
    font-size: 14px;
    font-weight: 500;
    color: #555;
    margin-bottom: 5px;
  }
  
  input, select, textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: #007AFF;
      box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 80px;
  }
  
  .checkbox-container {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 5px;
    
    input[type="checkbox"] {
      width: auto;
    }
    
    label {
      margin: 0;
      font-weight: normal;
    }
  }
`;

const EditModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  
  button {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    
    &.save {
      background: #007AFF;
      color: white;
      
      &:hover {
        background: #0056b3;
      }
    }
    
    &.cancel {
      background: #6c757d;
      color: white;
      
      &:hover {
        background: #545b62;
      }
    }
  }
`;

const PropertyCard = ({ property, userProfile, onViewBookings, onViewLogs, onCopyLink, onDelete, onUpdate }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [showOwnerSelector, setShowOwnerSelector] = useState(false);
  const [showOwnerCreator, setShowOwnerCreator] = useState(false);
  const [availableOwners, setAvailableOwners] = useState([]);
  const [allOwners, setAllOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [newOwnerData, setNewOwnerData] = useState({
    is_company: false,
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    email: '',
    mailing_address_line1: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip: '',
    contact_person_name: '' // For c/o on entities
  });
  
  // Resident state
  const [showResidentSelector, setShowResidentSelector] = useState(false);
  const [showResidentCreator, setShowResidentCreator] = useState(false);
  const [availableResidents, setAvailableResidents] = useState([]);
  const [allResidents, setAllResidents] = useState([]);
  const [loadingResidents, setLoadingResidents] = useState(false);
  const [newResidentData, setNewResidentData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    lease_start_date: '',
    lease_end_date: '',
    monthly_rent: '',
    security_deposit: ''
  });

  // Agent state - simplified to just track profile completion
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  
  // Debug: Log property data to see what we're receiving
  console.log('🏠 PropertyCard received property data:', property);
  console.log('🧑‍💼 Agent info:', {
    use_public_profile: property.use_public_profile,
    agent_first_name: property.agent_first_name,
    agent_last_name: property.agent_last_name,
    agent_phone: property.agent_phone,
    agent_email: property.agent_email
  });
  console.log('🏠 Owner info:', {
    owner_type: property.owner_type,
    owner_first_name: property.owner_first_name,
    owner_last_name: property.owner_last_name,
    owner_entity_name: property.owner_entity_name,
    owner_phone: property.owner_phone,
    owner_email: property.owner_email
  });
  console.log('👤 Resident info:', {
    occupancy_status: property.occupancy_status,
    is_occupied: property.is_occupied,
    resident_type: property.resident_type,
    resident_first_name: property.resident_first_name,
    resident_last_name: property.resident_last_name,
    resident_phone: property.resident_phone,
    resident_email: property.resident_email,
    tenant_name: property.tenant_name,
    tenant_phone: property.tenant_phone,
    tenant_email: property.tenant_email
  });
  
  const showingsCount = property.showingsBooked || property.showings || 0;
  const minutesSaved = showingsCount * 30;
  const hoursSaved = minutesSaved >= 60 ? Math.floor(minutesSaved / 60) : 0;
  const remainingMinutes = minutesSaved % 60;
  
  // Generate proper booking URL using secure slug
  const bookingUrl = property.slug 
    ? `${window.location.origin}/book/${property.slug}`
    : `${window.location.origin}/book/${property.id}`;
  
  
  const handleStatusChange = (e) => {
    console.log('Status change:', property.id, 'from', property.status, 'to', e.target.value);
    onUpdate(property.id, { status: e.target.value });
  };

  const handleInstructionsBlur = (e) => {
    if (e.target.value !== property.showing_instructions) {
      onUpdate(property.id, { showing_instructions: e.target.value });
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = bookingUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleVisitLink = () => {
    window.open(bookingUrl, '_blank');
  };

  const generateQRCode = async () => {
    try {
      const qrUrl = await QRCode.toDataURL(bookingUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrUrl);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Error generating QR code');
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qr-code-${property.address_line1?.replace(/[^a-zA-Z0-9]/g, '-') || property.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrCodeUrl('');
  };

  // Communication helpers
  const formatPhoneForTel = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Add +1 if it looks like a US number without country code
    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
  };

  const handleCall = (phone, name, type) => {
    if (!phone) return;
    const telUrl = `tel:${formatPhoneForTel(phone)}`;
    window.open(telUrl);
  };

  const handleSMS = (phone, name, type) => {
    if (!phone) return;
    const propertyAddress = `${property.address_line1 || property.address}${(property.unit_number || property.unitNumber) ? `, ${property.unit_number || property.unitNumber}` : ''}, ${property.city}, ${property.state}`;
    
    let message = '';
    if (type === 'owner') {
      message = `Hi ${name || 'there'}, this is regarding your property at ${propertyAddress}. I wanted to follow up about scheduling showings. Please let me know a good time to discuss. Thank you!`;
    } else if (type === 'resident') {
      message = `Hi ${name || 'there'}, this is regarding the property showing at ${propertyAddress}. I wanted to coordinate timing for the upcoming showing. Please let me know your availability. Thank you!`;
    } else {
      message = `Hi ${name || 'there'}, this is regarding the property at ${propertyAddress}. Please let me know if you have any questions. Thank you!`;
    }
    
    const smsUrl = `sms:${formatPhoneForTel(phone)}?body=${encodeURIComponent(message)}`;
    window.open(smsUrl);
  };

  const handleEmail = (email, name, type) => {
    if (!email) return;
    const propertyAddress = `${property.address_line1 || property.address}${(property.unit_number || property.unitNumber) ? `, ${property.unit_number || property.unitNumber}` : ''}, ${property.city}, ${property.state}`;
    
    let subject = '';
    let body = '';
    
    if (type === 'owner') {
      subject = `Property Showing Coordination - ${propertyAddress}`;
      body = `Hi ${name || 'there'},\n\nI hope this email finds you well. I wanted to reach out regarding your property at ${propertyAddress}.\n\nI'm coordinating showings for this property and wanted to discuss the process and any specific instructions you'd like me to follow.\n\nPlease let me know a good time to connect.\n\nThank you!\n\nBest regards`;
    } else if (type === 'resident') {
      subject = `Upcoming Property Showing - ${propertyAddress}`;
      body = `Hi ${name || 'there'},\n\nI hope this email finds you well. I wanted to coordinate with you regarding upcoming showings at ${propertyAddress}.\n\nI want to ensure we're respectful of your time and space during any scheduled showings.\n\nPlease let me know your preferred notice time and any scheduling preferences.\n\nThank you!\n\nBest regards`;
    } else {
      subject = `Property Inquiry - ${propertyAddress}`;
      body = `Hi ${name || 'there'},\n\nI hope this email finds you well. I wanted to follow up regarding ${propertyAddress}.\n\nPlease let me know if you have any questions or if there's anything I can help you with.\n\nThank you!\n\nBest regards`;
    }
    
    const emailUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(emailUrl);
  };

  const handleShare = () => {
    // Generate shareable URL
    const shareUrl = property.slug 
      ? `${window.location.origin}/property/share/${property.slug}`
      : `${window.location.origin}/property/share/${property.id}`;
    
    // Try native sharing API first (mobile)
    if (navigator.share) {
      navigator.share({
        title: `Property Showing - ${property.address_line1 || property.address}`,
        text: `Check out this property! Book a showing instantly.`,
        url: shareUrl
      }).catch(console.error);
    } else {
      // Fallback to copy link
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert('📋 Share link copied to clipboard!\n\n' + shareUrl + '\n\nShare this link with anyone to let them book showings directly.');
      }).catch(() => {
        // Ultimate fallback
        prompt('Copy this share link:', shareUrl);
      });
    }
  };
  

  const handleEditProperty = () => {
    // Initialize edit data with current property values
    setEditData({
      // Property details
      address_line1: property.address_line1 || property.address || '',
      unit_number: property.unit_number || property.unitNumber || '',
      city: property.city || '',
      state: property.state || '',
      zip_code: property.zip_code || '',
      
      // Agent information
      use_public_profile: property.use_public_profile || false,
      agent_first_name: property.agent_first_name || '',
      agent_last_name: property.agent_last_name || '',
      agent_phone: property.agent_phone || '',
      agent_email: property.agent_email || '',
      
      // Owner information
      owner_type: property.owner_type || 'individual',
      owner_first_name: property.owner_first_name || '',
      owner_last_name: property.owner_last_name || '',
      owner_entity_name: property.owner_entity_name || '',
      owner_entity_type: property.owner_entity_type || '',
      owner_phone: property.owner_phone || '',
      owner_email: property.owner_email || '',
      
      // Resident information
      occupancy_status: property.occupancy_status || 'vacant',
      resident_type: property.resident_type || 'tenant',
      resident_first_name: property.resident_first_name || '',
      resident_last_name: property.resident_last_name || '',
      resident_phone: property.resident_phone || '',
      resident_email: property.resident_email || '',
      tenant_name: property.tenant_name || '',
      tenant_phone: property.tenant_phone || '',
      tenant_email: property.tenant_email || '',
      
      // Property settings
      status: property.status || 'listed',
      booking_approval: property.booking_approval || 'automatic',
      showing_instructions: property.showing_instructions || ''
    });
    setShowEditModal(true);
  };

  const handleEditInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    
    // Update property with all edit data
    onUpdate(property.id, editData);
    setShowEditModal(false);
    setEditData({});
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditData({});
  };

  // Owner selector functions
  const handleAddOwner = async () => {
    console.log('👤 Opening owner selector');
    setLoadingOwners(true);
    setShowOwnerSelector(true);
    try {
      const owners = await showingbookerAPI.getOwners();
      console.log('📋 Loaded owners:', owners);
      setAllOwners(owners);
      setAvailableOwners(owners);
    } catch (error) {
      console.error('❌ Error loading owners:', error);
      setAllOwners([]);
      setAvailableOwners([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  const handleSelectOwner = async (owner) => {
    try {
      // Update property with selected owner information
      const ownerData = {
        owner_type: owner.is_company ? 'entity' : 'individual',
        owner_first_name: owner.first_name,
        owner_last_name: owner.last_name,
        owner_entity_name: owner.company_name,
        owner_phone: owner.phone,
        owner_email: owner.email,
        landlord_id: owner.id // Link to the owner record
      };
      
      await onUpdate(property.id, ownerData);
      setShowOwnerSelector(false);
    } catch (error) {
      console.error('Error linking owner:', error);
      alert('Failed to link owner: ' + error.message);
    }
  };

  const handleCreateNewOwner = () => {
    setShowOwnerSelector(false);
    setNewOwnerData({
      is_company: false,
      first_name: '',
      last_name: '',
      company_name: '',
      phone: '',
      email: '',
      mailing_address_line1: '',
      mailing_city: '',
      mailing_state: '',
      mailing_zip: '',
      contact_person_name: ''
    });
    setShowOwnerCreator(true);
  };

  const handleOwnerInputChange = (field, value) => {
    setNewOwnerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateOwnerSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create the owner
      const newOwner = await showingbookerAPI.createOwner(newOwnerData);
      
      // Link this new owner to the property
      const ownerData = {
        owner_type: newOwner.is_company ? 'entity' : 'individual',
        owner_first_name: newOwner.first_name,
        owner_last_name: newOwner.last_name,
        owner_entity_name: newOwner.company_name,
        owner_phone: newOwner.phone,
        owner_email: newOwner.email,
        landlord_id: newOwner.id
      };
      
      await onUpdate(property.id, ownerData);
      setShowOwnerCreator(false);
    } catch (error) {
      console.error('Error creating owner:', error);
      alert('Failed to create owner: ' + error.message);
    }
  };

  const closeOwnerModals = () => {
    setShowOwnerSelector(false);
    setShowOwnerCreator(false);
  };

  // Resident selector functions
  const handleAddResident = async () => {
    console.log('🏠 Opening resident selector');
    setLoadingResidents(true);
    setShowResidentSelector(true);
    try {
      const residents = await showingbookerAPI.getResidents();
      console.log('📋 Loaded residents:', residents);
      setAllResidents(residents);
      setAvailableResidents(residents);
    } catch (error) {
      console.error('❌ Error loading residents:', error);
      setAllResidents([]);
      setAvailableResidents([]);
    } finally {
      setLoadingResidents(false);
    }
  };

  const handleSelectResident = async (resident) => {
    try {
      // Update property with selected resident information
      const residentData = {
        occupancy_status: 'resident',
        resident_type: 'tenant',
        resident_first_name: resident.first_name,
        resident_last_name: resident.last_name,
        resident_phone: resident.phone,
        resident_email: resident.email,
        // Also update legacy fields for backward compatibility
        tenant_name: `${resident.first_name} ${resident.last_name}`,
        tenant_phone: resident.phone,
        tenant_email: resident.email
      };
      
      await onUpdate(property.id, residentData);
      setShowResidentSelector(false);
    } catch (error) {
      console.error('Error linking resident:', error);
      alert('Failed to link resident: ' + error.message);
    }
  };

  const handleCreateNewResident = () => {
    setShowResidentSelector(false);
    setNewResidentData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      lease_start_date: '',
      lease_end_date: '',
      monthly_rent: '',
      security_deposit: ''
    });
    setShowResidentCreator(true);
  };

  const handleResidentInputChange = (field, value) => {
    setNewResidentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateResidentSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create the resident
      const newResident = await showingbookerAPI.createResident(newResidentData);
      
      // Link this new resident to the property
      const residentData = {
        occupancy_status: 'resident',
        resident_type: 'tenant',
        resident_first_name: newResident.first_name,
        resident_last_name: newResident.last_name,
        resident_phone: newResident.phone,
        resident_email: newResident.email,
        // Also update legacy fields for backward compatibility
        tenant_name: `${newResident.first_name} ${newResident.last_name}`,
        tenant_phone: newResident.phone,
        tenant_email: newResident.email
      };
      
      await onUpdate(property.id, residentData);
      setShowResidentCreator(false);
    } catch (error) {
      console.error('Error creating resident:', error);
      alert('Failed to create resident: ' + error.message);
    }
  };

  const closeResidentModals = () => {
    setShowResidentSelector(false);
    setShowResidentCreator(false);
  };

  // Check if agent profile is complete (from ShowingBooker agent profile)
  const [agentProfile, setAgentProfile] = useState(null);
  
  useEffect(() => {
    // Load agent profile from localStorage
    try {
      const savedProfile = localStorage.getItem('showingbooker_agent_profile');
      if (savedProfile) {
        setAgentProfile(JSON.parse(savedProfile));
      }
    } catch (error) {
      console.error('Error loading agent profile:', error);
    }
  }, []);
  
  const isProfileComplete = agentProfile?.agent_name && agentProfile?.agent_phone;
  
  // Handle profile completion prompt
  const handleCompleteProfile = () => {
    alert('Please go to your Agent Profile section to complete your contact information. This ensures proper notifications and contact details on booking pages.');
  };

  // Disconnect contact functions
  const handleDisconnectOwner = async () => {
    if (window.confirm('Disconnect owner from this property? The owner will remain in your database but will no longer be associated with this property.')) {
      try {
        const disconnectData = {
          owner_type: null,
          owner_first_name: null,
          owner_last_name: null,
          owner_entity_name: null,
          owner_phone: null,
          owner_email: null,
          landlord_id: null
        };
        await onUpdate(property.id, disconnectData);
      } catch (error) {
        console.error('Error disconnecting owner:', error);
        alert('Failed to disconnect owner: ' + error.message);
      }
    }
  };

  const handleDisconnectResident = async () => {
    if (window.confirm('Disconnect resident from this property? The resident will remain in your database but will no longer be associated with this property.')) {
      try {
        const disconnectData = {
          occupancy_status: 'vacant',
          resident_type: null,
          resident_first_name: null,
          resident_last_name: null,
          resident_phone: null,
          resident_email: null,
          tenant_name: null,
          tenant_phone: null,
          tenant_email: null
        };
        await onUpdate(property.id, disconnectData);
      } catch (error) {
        console.error('Error disconnecting resident:', error);
        alert('Failed to disconnect resident: ' + error.message);
      }
    }
  };
  
  return (
    <Card>
      <PropertyHeader>
        <div>
          <PropertyTitle>
            <PropertyIcon>🏠</PropertyIcon>
            <PropertyAddress>
              {property.address_line1 || property.address}
              {(property.unit_number || property.unitNumber) && `, ${property.unit_number || property.unitNumber}`}
            </PropertyAddress>
          </PropertyTitle>
          <div style={{ fontSize: '14px', color: '#666', marginLeft: '30px' }}>
            {property.city}, {property.state} {property.zip_code}
          </div>
        </div>
      </PropertyHeader>
      
      {/* Property Photo */}
      {property.property_photo && (
        <PropertyPhoto>
          <img 
            src={property.property_photo} 
            alt={`Property at ${property.address_line1 || property.address}`}
            loading="lazy"
          />
        </PropertyPhoto>
      )}
      
      <PropertyDetails>
        <DetailRow>
          <span>Showing Status:</span>
          <InlineSelect value={property.status || 'listed'} onChange={handleStatusChange}>
            <option value="listed">Public</option>
            <option value="unlisted">Private</option>
          </InlineSelect>
        </DetailRow>
        
      </PropertyDetails>

      {/* Contacts Section */}
      <ContactsSection>
        <ContactsHeader>
          📞 Who should receive notifications?
        </ContactsHeader>
        
        <ContactsGrid>
          {/* Agent Column - Simplified */}
          <ContactColumn>
            <ContactTitle>🧑‍💼 Agent (You)</ContactTitle>
            <ContactInfo>
              <div className="name">
                {agentProfile?.agent_name || 'Your Agent Profile'}
              </div>
              <div className="details">
                {isProfileComplete ? (
                  <>
                    {agentProfile.agent_phone && <div>📱 {agentProfile.agent_phone}</div>}
                    {agentProfile.agent_email && <div>✉️ {agentProfile.agent_email}</div>}
                    {agentProfile.agent_license && <div>🆔 {agentProfile.agent_license}</div>}
                  </>
                ) : (
                  <div style={{ color: '#dc3545', fontSize: '12px' }}>
                    Profile incomplete - complete for notifications
                  </div>
                )}
              </div>
            </ContactInfo>
            
            {isProfileComplete && (
              <ContactActions>
                {agentProfile.agent_phone && (
                  <>
                    <CommButton 
                      className="call" 
                      onClick={() => handleCall(agentProfile.agent_phone, agentProfile.agent_name, 'agent')}
                    >
                      Call
                    </CommButton>
                    <CommButton 
                      className="sms" 
                      onClick={() => handleSMS(agentProfile.agent_phone, agentProfile.agent_name, 'agent')}
                    >
                      Text
                    </CommButton>
                  </>
                )}
                {agentProfile.agent_email && (
                  <CommButton 
                    className="email" 
                    onClick={() => handleEmail(agentProfile.agent_email, agentProfile.agent_name, 'agent')}
                  >
                    Email
                  </CommButton>
                )}
              </ContactActions>
            )}
            
            <ContactManagement>
              {!isProfileComplete ? (
                <ManageButton className="add" onClick={handleCompleteProfile} style={{ background: '#dc3545' }}>
                  ⚠️ Complete Profile
                </ManageButton>
              ) : (
                <ManageButton className="edit" onClick={() => alert('Profile looks good! Edit from Profile section if needed.')} style={{ background: '#28a745' }}>
                  ✅ Profile Complete
                </ManageButton>
              )}
            </ContactManagement>
          </ContactColumn>

          {/* Resident Column */}
          <ContactColumn>
            <ContactTitle>🏠 Resident</ContactTitle>
            <ContactInfo>
              {(property.occupancy_status === 'resident' || property.is_occupied) ? (
                <>
                  <div className="name">
                    {property.tenant_name || `${property.resident_first_name || ''} ${property.resident_last_name || ''}`.trim() || 'Not specified'}
                  </div>
                  {((property.tenant_phone || property.resident_phone) || (property.tenant_email || property.resident_email)) && (
                    <div className="details">
                      {(property.tenant_phone || property.resident_phone) && (
                        <div>📱 {property.tenant_phone || property.resident_phone}</div>
                      )}
                      {(property.tenant_email || property.resident_email) && (
                        <div>✉️ {property.tenant_email || property.resident_email}</div>
                      )}
                      <div>👤 {property.resident_type === 'tenant' ? 'Tenant' : property.resident_type === 'owner' ? 'Owner' : 'Tenant'}</div>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-info">
                  {property.occupancy_status === 'vacant' ? 'Property is vacant' : 'No resident information'}
                </div>
              )}
            </ContactInfo>
            {(property.occupancy_status === 'resident' || property.is_occupied) && ((property.tenant_phone || property.resident_phone) || (property.tenant_email || property.resident_email)) && (
              <ContactActions>
                {(property.tenant_phone || property.resident_phone) && (
                  <>
                    <CommButton 
                      className="call" 
                      onClick={() => handleCall(property.tenant_phone || property.resident_phone, property.tenant_name || property.resident_first_name, 'resident')}
                    >
                      Call
                    </CommButton>
                    <CommButton 
                      className="sms" 
                      onClick={() => handleSMS(property.tenant_phone || property.resident_phone, property.tenant_name || property.resident_first_name, 'resident')}
                    >
                      Text
                    </CommButton>
                  </>
                )}
                {(property.tenant_email || property.resident_email) && (
                  <CommButton 
                    className="email" 
                    onClick={() => handleEmail(property.tenant_email || property.resident_email, property.tenant_name || property.resident_first_name, 'resident')}
                  >
                    Email
                  </CommButton>
                )}
              </ContactActions>
            )}
            <ContactManagement>
              {(property.occupancy_status === 'resident' || property.is_occupied) && ((property.tenant_phone || property.resident_phone) || (property.tenant_email || property.resident_email)) ? (
                <>
                  <ManageButton className="edit" onClick={handleAddResident}>
                    ✏️ Edit Resident
                  </ManageButton>
                  <ManageButton 
                    className="delete" 
                    onClick={() => handleDisconnectResident()}
                    style={{ background: '#dc3545', marginLeft: '8px' }}
                  >
                    🔗 Disconnect
                  </ManageButton>
                </>
              ) : (
                <ManageButton className="add" onClick={handleAddResident}>
                  ➕ Add Resident
                </ManageButton>
              )}
            </ContactManagement>
          </ContactColumn>

          {/* Owner Column */}
          <ContactColumn>
            <ContactTitle>👤 Owner</ContactTitle>
            <ContactInfo>
              {(property.owner_first_name || property.owner_last_name || property.owner_entity_name) ? (
                <>
                  <div className="name">
                    {property.owner_type === 'entity' ? (
                      `${property.owner_entity_name || 'Entity'}`
                    ) : (
                      `${property.owner_first_name || ''} ${property.owner_last_name || ''}`.trim() || 'Not specified'
                    )}
                  </div>
                  {(property.owner_phone || property.owner_email || property.owner_entity_type) && (
                    <div className="details">
                      {property.owner_phone && <div>📱 {property.owner_phone}</div>}
                      {property.owner_email && <div>✉️ {property.owner_email}</div>}
                      {property.owner_entity_type && <div>🏢 {property.owner_entity_type}</div>}
                    </div>
                  )}
                </>
              ) : (
                <div className="no-info">No owner information</div>
              )}
            </ContactInfo>
            {(property.owner_phone || property.owner_email) && (
              <ContactActions>
                {property.owner_phone && (
                  <>
                    <CommButton 
                      className="call" 
                      onClick={() => handleCall(property.owner_phone, property.owner_first_name || property.owner_entity_name, 'owner')}
                    >
                      Call
                    </CommButton>
                    <CommButton 
                      className="sms" 
                      onClick={() => handleSMS(property.owner_phone, property.owner_first_name || property.owner_entity_name, 'owner')}
                    >
                      Text
                    </CommButton>
                  </>
                )}
                {property.owner_email && (
                  <CommButton 
                    className="email" 
                    onClick={() => handleEmail(property.owner_email, property.owner_first_name || property.owner_entity_name, 'owner')}
                  >
                    Email
                  </CommButton>
                )}
              </ContactActions>
            )}
            <ContactManagement>
              {(property.owner_phone || property.owner_email) ? (
                <>
                  <ManageButton className="edit" onClick={handleAddOwner}>
                    ✏️ Edit Owner
                  </ManageButton>
                  <ManageButton 
                    className="delete" 
                    onClick={() => handleDisconnectOwner()}
                    style={{ background: '#dc3545', marginLeft: '8px' }}
                  >
                    🔗 Disconnect
                  </ManageButton>
                </>
              ) : (
                <ManageButton className="add" onClick={handleAddOwner}>
                  ➕ Add Owner
                </ManageButton>
              )}
            </ContactManagement>
          </ContactColumn>
        </ContactsGrid>
      </ContactsSection>

      {/* Booking and Instructions Section */}
      <PropertyDetails>
        <DetailRow>
          <span>Booking:</span>
          <span>{property.booking_approval === 'automatic' ? 'Auto-Approval' : 'Manual Approval'}</span>
        </DetailRow>
        
        <DetailRow>
          <span>Instructions:</span>
          <EditableText
            defaultValue={property.showing_instructions || ''}
            onBlur={handleInstructionsBlur}
            placeholder="Click to edit showing instructions..."
            rows="2"
          />
        </DetailRow>
      </PropertyDetails>

      {/* Public Booking Link Section */}
      <BookingLinkContainer>
        <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#0066cc' }}>
          Public Booking Link
        </div>
        <BookingLinkText>{bookingUrl}</BookingLinkText>
        <LinkButtons>
          <LinkButton className="copy" onClick={handleCopyLink}>
            {linkCopied ? 'Copied!' : 'Copy Link'}
          </LinkButton>
          <LinkButton className="visit" onClick={handleVisitLink}>
            View Booking Page
          </LinkButton>
        </LinkButtons>
        <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
          Secure link - Share with buyers and agents to book showings
        </div>
      </BookingLinkContainer>
      
      <StatsContainer>
        <StatBox>
          <StatNumber>{showingsCount}</StatNumber>
          <StatLabel>Showings Booked</StatLabel>
        </StatBox>
        <StatBox>
          <StatNumber>
            {minutesSaved < 60 
              ? `${minutesSaved} min` 
              : hoursSaved > 0 && remainingMinutes > 0 
                ? `${hoursSaved}.5 hrs` 
                : `${hoursSaved}+ hrs`
            }
          </StatNumber>
          <StatLabel>Time Saved</StatLabel>
        </StatBox>
      </StatsContainer>
      
      <ButtonContainer>
        <Button className="primary" onClick={handleEditProperty}>
          Edit Property
        </Button>
        <Button className="secondary" onClick={() => onViewBookings(property.id)}>
          View Bookings
        </Button>
        <Button className="secondary" onClick={() => onViewLogs(property.id)}>
          Compliance Logs
        </Button>
        <Button className="secondary" onClick={generateQRCode}>
          QR Code
        </Button>
        <Button className="secondary" onClick={() => handleShare()}>
          Share
        </Button>
        <Button 
          className={showDeleteConfirm ? "danger" : "secondary"}
          onClick={() => {
            if (showDeleteConfirm) {
              onDelete(property.id);
              setShowDeleteConfirm(false);
            } else {
              setShowDeleteConfirm(true);
            }
          }}
          title="Delete property"
        >
          {showDeleteConfirm ? '❌ CONFIRM DELETE' : '🗑️ Delete'}
        </Button>
        {showDeleteConfirm && (
          <Button className="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
        )}
      </ButtonContainer>
      
      {showDeleteConfirm && (
        <WarningBox>
          <strong>Warning:</strong> Deleting this property is not reversible. 
          All associated showings and notifications will be permanently removed.
        </WarningBox>
      )}
      
      {/* QR Code Modal */}
      {showQRModal && (
        <QRModal onClick={closeQRModal}>
          <QRModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Property QR Code</h3>
            <div className="property-info">
              <strong>{property.address_line1 || property.address}</strong>
              {(property.unit_number || property.unitNumber) && `, ${property.unit_number || property.unitNumber}`}
              <br />
              {property.city}, {property.state} {property.zip_code}
            </div>
            <div className="qr-container">
              {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code for property booking" />}
            </div>
            <p style={{ fontSize: '14px', color: '#666', margin: '10px 0' }}>
              Scan this QR code to book a showing instantly
            </p>
            <div className="buttons">
              <button className="download" onClick={downloadQRCode}>
                📥 Download QR Code
              </button>
              <button className="close" onClick={closeQRModal}>
                Close
              </button>
            </div>
          </QRModalContent>
        </QRModal>
      )}

      {/* Edit Property Modal */}
      {showEditModal && (
        <EditModal onClick={handleCancelEdit}>
          <EditModalContent onClick={(e) => e.stopPropagation()}>
            <EditPropertyForm
              property={property}
              onSubmit={async (formData) => {
                await onUpdate(property.id, formData);
                setShowEditModal(false);
              }}
              onCancel={handleCancelEdit}
            />
          </EditModalContent>
        </EditModal>
      )}

      {/* Owner Selector Modal */}
      {showOwnerSelector && (
        <SelectorModal onClick={closeOwnerModals}>
          <SelectorContent onClick={(e) => e.stopPropagation()}>
            <SelectorHeader>
              <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                👤 Select Owner
              </h3>
              <button
                onClick={closeOwnerModals}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </SelectorHeader>

            <SearchBox
              type="text"
              placeholder="🔍 Search owners by name or company..."
              onChange={(e) => {
                const searchTerm = e.target.value.toLowerCase();
                if (searchTerm) {
                  const filtered = allOwners.filter(owner => 
                    (owner.first_name?.toLowerCase().includes(searchTerm)) ||
                    (owner.last_name?.toLowerCase().includes(searchTerm)) ||
                    (owner.company_name?.toLowerCase().includes(searchTerm)) ||
                    (owner.email?.toLowerCase().includes(searchTerm))
                  );
                  setAvailableOwners(filtered);
                } else {
                  // Reset to all owners if search is cleared
                  setAvailableOwners(allOwners);
                }
              }}
            />

            <CreateNewButton onClick={handleCreateNewOwner}>
              ➕ Create New Owner
            </CreateNewButton>

            <OwnersList>
              {loadingOwners ? (
                <LoadingMessage>Loading owners...</LoadingMessage>
              ) : availableOwners.length === 0 ? (
                <EmptyMessage>
                  No owners found. Create your first owner to get started!
                </EmptyMessage>
              ) : (
                availableOwners.map(owner => (
                  <OwnerItem key={owner.id} onClick={() => handleSelectOwner(owner)}>
                    <div className="name">
                      {owner.is_company ? 
                        owner.company_name : 
                        `${owner.first_name} ${owner.last_name}`
                      }
                    </div>
                    <div className="details">
                      {owner.is_company ? 'Company' : 'Individual'}
                      {owner.phone && ` • 📱 ${owner.phone}`}
                      {owner.email && ` • ✉️ ${owner.email}`}
                    </div>
                  </OwnerItem>
                ))
              )}
            </OwnersList>

            <div style={{ textAlign: 'center', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
              <button
                onClick={closeOwnerModals}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </SelectorContent>
        </SelectorModal>
      )}

      {/* Owner Creator Modal */}
      {showOwnerCreator && (
        <SelectorModal onClick={closeOwnerModals}>
          <SelectorContent onClick={(e) => e.stopPropagation()}>
            <SelectorHeader>
              <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                ➕ Create New Owner
              </h3>
              <button
                onClick={closeOwnerModals}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </SelectorHeader>

            <form onSubmit={handleCreateOwnerSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Owner Type
                </label>
                <select
                  value={newOwnerData.is_company ? 'company' : 'individual'}
                  onChange={(e) => handleOwnerInputChange('is_company', e.target.value === 'company')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  required
                >
                  <option value="individual">Individual</option>
                  <option value="company">Company</option>
                </select>
              </div>

              {newOwnerData.is_company ? (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={newOwnerData.company_name}
                      onChange={(e) => handleOwnerInputChange('company_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Contact Person (c/o)
                    </label>
                    <input
                      type="text"
                      value={newOwnerData.contact_person_name}
                      onChange={(e) => handleOwnerInputChange('contact_person_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      placeholder="Contact person name for this entity"
                    />
                  </div>
                </>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={newOwnerData.first_name}
                      onChange={(e) => handleOwnerInputChange('first_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={newOwnerData.last_name}
                      onChange={(e) => handleOwnerInputChange('last_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      required
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newOwnerData.phone}
                    onChange={(e) => handleOwnerInputChange('phone', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={newOwnerData.email}
                    onChange={(e) => handleOwnerInputChange('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Mailing Address
                </label>
                <input
                  type="text"
                  value={newOwnerData.mailing_address_line1}
                  onChange={(e) => handleOwnerInputChange('mailing_address_line1', e.target.value)}
                  placeholder="Street address"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '8px'
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    value={newOwnerData.mailing_city}
                    onChange={(e) => handleOwnerInputChange('mailing_city', e.target.value)}
                    placeholder="City"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <input
                    type="text"
                    value={newOwnerData.mailing_state}
                    onChange={(e) => handleOwnerInputChange('mailing_state', e.target.value)}
                    placeholder="State"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <input
                    type="text"
                    value={newOwnerData.mailing_zip}
                    onChange={(e) => handleOwnerInputChange('mailing_zip', e.target.value)}
                    placeholder="ZIP"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end',
                paddingTop: '15px',
                borderTop: '1px solid #e9ecef'
              }}>
                <button
                  type="button"
                  onClick={closeOwnerModals}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: '#007AFF',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Create & Link Owner
                </button>
              </div>
            </form>
          </SelectorContent>
        </SelectorModal>
      )}

      {/* Resident Selector Modal */}
      {showResidentSelector && (
        <SelectorModal onClick={closeResidentModals}>
          <SelectorContent onClick={(e) => e.stopPropagation()}>
            <SelectorHeader>
              <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                🏠 Select Resident
              </h3>
              <button
                onClick={closeResidentModals}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </SelectorHeader>

            <SearchBox
              type="text"
              placeholder="🔍 Search residents by name or email..."
              onChange={(e) => {
                const searchTerm = e.target.value.toLowerCase();
                if (searchTerm) {
                  const filtered = allResidents.filter(resident => 
                    (resident.first_name?.toLowerCase().includes(searchTerm)) ||
                    (resident.last_name?.toLowerCase().includes(searchTerm)) ||
                    (resident.email?.toLowerCase().includes(searchTerm)) ||
                    (resident.phone?.toLowerCase().includes(searchTerm))
                  );
                  setAvailableResidents(filtered);
                } else {
                  // Reset to all residents if search is cleared
                  setAvailableResidents(allResidents);
                }
              }}
            />

            <CreateNewButton onClick={handleCreateNewResident}>
              ➕ Create New Resident
            </CreateNewButton>

            <OwnersList>
              {loadingResidents ? (
                <LoadingMessage>Loading residents...</LoadingMessage>
              ) : availableResidents.length === 0 ? (
                <EmptyMessage>
                  No residents found. Create your first resident to get started!
                </EmptyMessage>
              ) : (
                availableResidents.map(resident => (
                  <OwnerItem key={resident.id} onClick={() => handleSelectResident(resident)}>
                    <div className="name">
                      {`${resident.first_name} ${resident.last_name}`}
                    </div>
                    <div className="details">
                      Tenant
                      {resident.phone && ` • 📱 ${resident.phone}`}
                      {resident.email && ` • ✉️ ${resident.email}`}
                      {resident.lease_start_date && ` • 📅 ${new Date(resident.lease_start_date).toLocaleDateString()}`}
                    </div>
                  </OwnerItem>
                ))
              )}
            </OwnersList>

            <div style={{ textAlign: 'center', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
              <button
                onClick={closeResidentModals}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </SelectorContent>
        </SelectorModal>
      )}

      {/* Resident Creator Modal */}
      {showResidentCreator && (
        <SelectorModal onClick={closeResidentModals}>
          <SelectorContent onClick={(e) => e.stopPropagation()}>
            <SelectorHeader>
              <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                ➕ Create New Resident
              </h3>
              <button
                onClick={closeResidentModals}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </SelectorHeader>

            <form onSubmit={handleCreateResidentSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={newResidentData.first_name}
                    onChange={(e) => handleResidentInputChange('first_name', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={newResidentData.last_name}
                    onChange={(e) => handleResidentInputChange('last_name', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newResidentData.phone}
                    onChange={(e) => handleResidentInputChange('phone', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={newResidentData.email}
                    onChange={(e) => handleResidentInputChange('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Emergency Contact
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    value={newResidentData.emergency_contact_name}
                    onChange={(e) => handleResidentInputChange('emergency_contact_name', e.target.value)}
                    placeholder="Emergency contact name"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <input
                    type="tel"
                    value={newResidentData.emergency_contact_phone}
                    onChange={(e) => handleResidentInputChange('emergency_contact_phone', e.target.value)}
                    placeholder="Phone"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Lease Information
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <input
                      type="date"
                      value={newResidentData.lease_start_date}
                      onChange={(e) => handleResidentInputChange('lease_start_date', e.target.value)}
                      placeholder="Start date"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ color: '#666' }}>Lease start</small>
                  </div>
                  <div>
                    <input
                      type="date"
                      value={newResidentData.lease_end_date}
                      onChange={(e) => handleResidentInputChange('lease_end_date', e.target.value)}
                      placeholder="End date"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ color: '#666' }}>Lease end</small>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <input
                      type="number"
                      value={newResidentData.monthly_rent}
                      onChange={(e) => handleResidentInputChange('monthly_rent', e.target.value)}
                      placeholder="Monthly rent"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ color: '#666' }}>Monthly rent</small>
                  </div>
                  <div>
                    <input
                      type="number"
                      value={newResidentData.security_deposit}
                      onChange={(e) => handleResidentInputChange('security_deposit', e.target.value)}
                      placeholder="Security deposit"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ color: '#666' }}>Security deposit</small>
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end',
                paddingTop: '15px',
                borderTop: '1px solid #e9ecef'
              }}>
                <button
                  type="button"
                  onClick={closeResidentModals}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: '#007AFF',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Create & Link Resident
                </button>
              </div>
            </form>
          </SelectorContent>
        </SelectorModal>
      )}

    </Card>
  );
};

export default PropertyCard;